import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePost, useGet, useGetLazy } from "../../api";
import {
  IAffiliateNetworksResponse,
  IDiscoveryResult,
  IAdvertisersResponse,
  IAffiliateAdvertiser,
  IOperationState,
  ISyncConfiguration,
  ISyncConfigMap,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Form,
  Row,
  Col,
  Alert,
  Card,
  Modal,
  Pagination,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  Search,
  Zap,
  RefreshCw,
  Settings,
  CheckCircle,
  XCircle,
  Plus,
  Download,
} from "react-feather";
import { settingsType, useSettings } from "../../hooks";
import { Link } from "react-router";
import { ManualAdvertiserModal } from "./manual-components";

const merchantStatusLabel = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    0: { bg: "secondary", label: "Unknown" },
    1: { bg: "success", label: "Active" },
    2: { bg: "danger", label: "Inactive" },
    3: { bg: "warning", label: "Pending" },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return <Badge bg={info.bg}>{info.label}</Badge>;
};

const AdvertiserTable: React.FC<{
  advertisers: IAffiliateAdvertiser[];
  onSelect?: (adv: IAffiliateAdvertiser) => void;
  syncConfigMap?: ISyncConfigMap;
}> = ({ advertisers, onSelect, syncConfigMap }) => (
  <Table striped bordered hover size="sm" style={{ fontSize: "0.85rem" }}>
    <thead>
      <tr>
        <th>Network</th>
        <th>ID</th>
        <th>Name</th>
        <th>Domain</th>
        <th>Status</th>
        <th>Products</th>
        <th>Region</th>
        <th>Sync</th>
        <th>Config</th>
      </tr>
    </thead>
    <tbody>
      {advertisers.map((adv) => {
        const configKey = `${adv.affiliateNetwork}-${adv.advertiserId}`;
        const config = syncConfigMap?.[configKey];
        return (
          <tr key={adv.id}>
            <td>
              <Badge bg="dark">{adv.affiliateNetwork}</Badge>
            </td>
            <td>{adv.advertiserId}</td>
            <td>
              <Link
                to={`/affiliate-products/advertiser/${adv.affiliateNetwork}/${adv.advertiserId}`}
                onClick={(e) => e.stopPropagation()}
              >
                <strong>{adv.name}</strong>
                {adv.region && (
                  <span className="text-muted"> ({adv.region})</span>
                )}
              </Link>
            </td>
            <td>
              <small className="text-muted">{adv.domain || "—"}</small>
            </td>
            <td>{merchantStatusLabel(adv.status)}</td>
            <td>{adv.productCount.toLocaleString()}</td>
            <td>{adv.region || "—"}</td>
            <td>
              {adv.affiliateNetwork === "Manual" ? (
                <Badge bg="info">Manual</Badge>
              ) : config ? (
                config.productIngestionEnabled ? (
                  <Badge bg="success">Syncing</Badge>
                ) : (
                  <Badge bg="warning" text="dark">
                    Categories Only
                  </Badge>
                )
              ) : (
                <Badge bg="secondary">Not Configured</Badge>
              )}
            </td>
            <td>
              {onSelect && adv.affiliateNetwork !== "Manual" ? (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => onSelect(adv)}
                  aria-label={`Configure ${adv.name}`}
                  title="Configure sync"
                >
                  <Settings size={12} />
                </Button>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
          </tr>
        );
      })}
      {advertisers.length === 0 && (
        <tr>
          <td colSpan={9} className="text-center text-muted">
            No advertisers found.
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);

export const Discovery: React.FC = () => {
  const discoveryPost = usePost<IDiscoveryResult[]>();
  const configPost = usePost<ISyncConfiguration>();
  const [hasLoadedAdvertisers, setHasLoadedAdvertisers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState("25");
  const [searchInput, setSearchInput] = useState("");
  const [domainInput, setDomainInput] = useState("");

  // Committed filter values — only updated when the user explicitly searches or loads
  const [committedFilters, setCommittedFilters] = useState<{
    query: string;
    domain: string;
    network: string;
  } | null>(null);

  const [selectedNetwork, setSelectedNetwork] = useSettings(
    settingsType.AP_DISCOVERY_NETWORK
  );
  const pageSizeNumber = Number(pageSize) || 25;
  const pageOffset = (currentPage - 1) * pageSizeNumber;

  const networks = useGet<IAffiliateNetworksResponse>(
    "affiliate-products/networks",
    true
  );

  const discoverableNetworks = useMemo(
    () => networks.data?.items || [],
    [networks.data]
  );
  const advertiserNetworkOptions = useMemo(() => {
    const options = [...discoverableNetworks];
    if (!options.includes("Manual")) {
      options.push("Manual");
    }
    return options;
  }, [discoverableNetworks]);

  // A fire-and-forget run answers without counts — only show rows that have them.
  const discoveryResults = useMemo(
    () => (discoveryPost.data ?? []).filter((r) => r?.affiliateNetwork),
    [discoveryPost.data]
  );

  const network = selectedNetwork || "";

  // Sync config modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showManualAdvertiserModal, setShowManualAdvertiserModal] =
    useState(false);
  const [selectedAdv, setSelectedAdv] = useState<IAffiliateAdvertiser | null>(
    null
  );
  const isManualNetwork = network === "Manual";
  const [modalCategories, setModalCategories] = useState("");
  const [configError, setConfigError] = useState<string | null>(null);

  // Every config, not just the first page — there are more of them than any one
  // page holds, and a missing row reads as "Not Configured".
  const syncConfigs = useGet<ISyncConfigMap>(
    `affiliate-products/sync/configs/map${network ? `?network=${encodeURIComponent(network)}` : ""}`,
    false
  );
  const syncConfigMap = syncConfigs.data ?? {};

  // The modal needs the whole config, so it is fetched per advertiser on open.
  const syncConfigGet = useGetLazy<ISyncConfiguration>();
  const [selectedConfig, setSelectedConfig] =
    useState<ISyncConfiguration | null>(null);

  // Operation status — auto-fetches on load
  const discoveryStatus = useGet<IOperationState[]>(
    "affiliate-products/status/discovery",
    false
  );

  // Single unified advertisers URL built from committed filters
  const advertisersUrl = useMemo(() => {
    if (!hasLoadedAdvertisers || !committedFilters) return null;
    const params = new URLSearchParams();
    params.set("offset", String(pageOffset));
    params.set("limit", String(pageSizeNumber));
    if (committedFilters.network)
      params.set("network", committedFilters.network);
    if (committedFilters.query.length >= 2)
      params.set("q", committedFilters.query);
    if (committedFilters.domain.length >= 2)
      params.set("domain", committedFilters.domain);
    return `affiliate-products/advertisers?${params.toString()}`;
  }, [hasLoadedAdvertisers, committedFilters, pageOffset, pageSizeNumber]);

  const advertisersList = useGet<IAdvertisersResponse>(advertisersUrl, false);
  const refetchAdvertisersList = advertisersList.refetch;

  // Check if any discovery is currently running
  const runningOps = (discoveryStatus.data || []).filter((s) => s.isRunning);

  const commitAndLoad = useCallback(
    async (overrideNetwork?: string) => {
      const filters = {
        query: searchInput.trim(),
        domain: domainInput.trim(),
        network: overrideNetwork !== undefined ? overrideNetwork : network,
      };
      setCurrentPage(1);
      setHasLoadedAdvertisers(true);
      setCommittedFilters(filters);
      await syncConfigs.refetch();
    },
    [searchInput, domainInput, network, syncConfigs]
  );

  const handleSearch = useCallback(() => {
    const q = searchInput.trim();
    const d = domainInput.trim();
    if (q.length >= 2 || d.length >= 2) {
      commitAndLoad();
    }
  }, [searchInput, domainInput, commitAndLoad]);

  const handleClearSearch = useCallback(() => {
    setCurrentPage(1);
    setSearchInput("");
    setDomainInput("");
    setHasLoadedAdvertisers(false);
    setCommittedFilters(null);
  }, []);

  const handleRefreshAdvertisers = useCallback(async () => {
    setCurrentPage(1);
    setHasLoadedAdvertisers(true);
    setCommittedFilters({
      query: "",
      domain: "",
      network,
    });
    await Promise.all([syncConfigs.refetch(), discoveryStatus.refetch()]);
  }, [network, syncConfigs, discoveryStatus]);

  const handleRunDiscovery = useCallback(
    async (net?: string) => {
      try {
        const url = net
          ? `affiliate-products/discovery/run?network=${net}`
          : "affiliate-products/discovery/run";
        await discoveryPost.execute(url);
        discoveryStatus.refetch();
        if (hasLoadedAdvertisers) refetchAdvertisersList();
      } catch (e: any) {
        discoveryStatus.refetch();
        console.error("Discovery failed:", e);
      }
    },
    [
      discoveryPost,
      discoveryStatus,
      hasLoadedAdvertisers,
      refetchAdvertisersList,
    ]
  );

  const loadSelectedConfig = useCallback(
    async (adv: IAffiliateAdvertiser) => {
      try {
        const config = await syncConfigGet.execute(
          `affiliate-products/sync/config/${adv.affiliateNetwork}/${adv.advertiserId}`
        );
        setSelectedConfig(config ?? null);
        return config ?? null;
      } catch {
        // 404 simply means this advertiser has never been configured.
        setSelectedConfig(null);
        return null;
      }
    },
    [syncConfigGet]
  );

  const handleSelectAdvertiser = useCallback(
    async (adv: IAffiliateAdvertiser) => {
      setSelectedAdv(adv);
      setSelectedConfig(null);
      setConfigError(null);
      setModalCategories("");
      setShowConfigModal(true);
      const existing = await loadSelectedConfig(adv);
      setModalCategories(existing?.categoryWhitelist?.join(", ") || "");
    },
    [loadSelectedConfig]
  );

  const handleCreateOrUpdateConfig = useCallback(async () => {
    if (!selectedAdv) return;
    try {
      setConfigError(null);
      let url = `affiliate-products/sync/config/${selectedAdv.affiliateNetwork}/${selectedAdv.advertiserId}`;
      if (modalCategories.trim()) {
        url += `?categories=${encodeURIComponent(modalCategories.trim())}`;
      }
      await configPost.execute(url);
      await Promise.all([
        syncConfigs.refetch(),
        loadSelectedConfig(selectedAdv),
      ]);
    } catch (e: any) {
      setConfigError(e.message || "Failed to save config");
    }
  }, [
    configPost,
    selectedAdv,
    modalCategories,
    syncConfigs,
    loadSelectedConfig,
  ]);

  const handleToggleProducts = useCallback(async () => {
    if (!selectedAdv || !selectedConfig) return;
    try {
      setConfigError(null);
      const action = selectedConfig.productIngestionEnabled
        ? "disable-products"
        : "enable-products";
      await configPost.execute(
        `affiliate-products/sync/config/${selectedAdv.affiliateNetwork}/${selectedAdv.advertiserId}/${action}`
      );
      await Promise.all([
        syncConfigs.refetch(),
        loadSelectedConfig(selectedAdv),
      ]);
    } catch (e: any) {
      setConfigError(e.message || "Failed to toggle products");
    }
  }, [
    configPost,
    selectedAdv,
    selectedConfig,
    syncConfigs,
    loadSelectedConfig,
  ]);

  const { execute: fetchAdvertisersCsv } = useGetLazy<string>();
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Full export across every network, independent of the on-screen filters.
  const handleDownloadCsv = useCallback(async () => {
    setDownloadingCsv(true);
    setCsvError(null);
    try {
      const csv = await fetchAdvertisersCsv(
        "affiliate-products/advertisers/export",
        { responseType: "text" }
      );
      if (csv == null) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-advertisers-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setCsvError(e?.message || "Failed to download CSV");
    } finally {
      setDownloadingCsv(false);
    }
  }, [fetchAdvertisersCsv]);

  const isSearchActive =
    committedFilters != null &&
    (committedFilters.query.length >= 2 || committedFilters.domain.length >= 2);

  useEffect(() => {
    if (hasLoadedAdvertisers) {
      refetchAdvertisersList();
    }
  }, [
    hasLoadedAdvertisers,
    currentPage,
    pageSize,
    advertisersUrl,
    refetchAdvertisersList,
  ]);

  const displayAdvertisers = advertisersList.data?.advertisers || [];
  const totalAdvertisers = advertisersList.data?.totalCount || 0;
  const isLoading = advertisersList.loading;
  const totalPages = Math.max(1, Math.ceil(totalAdvertisers / pageSizeNumber));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = totalAdvertisers === 0 ? 0 : pageOffset + 1;
  const pageEndIndex = Math.min(
    pageOffset + displayAdvertisers.length,
    totalAdvertisers
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [network, isSearchActive, pageSize]);

  const normalizedSearchInput = searchInput.trim();
  const normalizedDomainInput = domainInput.trim();

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) {
      return [] as number[];
    }

    const windowSize = 5;
    const start = Math.max(1, safeCurrentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const adjustedStart = Math.max(1, end - windowSize + 1);
    return Array.from(
      { length: end - adjustedStart + 1 },
      (_, i) => adjustedStart + i
    );
  }, [safeCurrentPage, totalPages]);

  return (
    <div>
      <h4 className="mb-3">Advertiser Discovery</h4>

      {/* Discovery Triggers */}
      {!isManualNetwork && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title className="h6">Run Discovery</Card.Title>
            <p className="small text-muted mb-2">
              Discover available advertisers from affiliate networks and store
              them.
            </p>
            <div className="d-flex gap-2 align-items-center">
              <Dropdown as={ButtonGroup} size="sm">
                <Button
                  variant="primary"
                  onClick={() => handleRunDiscovery()}
                  disabled={discoveryPost.loading}
                  title="Run discovery across every network in one pass"
                >
                  <Zap size={14} className="me-1" />
                  All Networks
                </Button>
                <Dropdown.Toggle
                  split
                  variant="primary"
                  disabled={
                    discoveryPost.loading || !discoverableNetworks.length
                  }
                  title="Run discovery for a single network"
                />
                <Dropdown.Menu>
                  <Dropdown.Header>Run one network</Dropdown.Header>
                  {discoverableNetworks.map((n) => (
                    <Dropdown.Item
                      key={n}
                      onClick={() => handleRunDiscovery(n)}
                    >
                      {n}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {discoveryPost.loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </div>

            {discoveryPost.data && (
              <Alert variant="success" className="mt-3 mb-0">
                {discoveryResults.length ? (
                  discoveryResults.map((r) => (
                    <div key={r.affiliateNetwork}>
                      <strong>{r.affiliateNetwork}:</strong> +{r.inserted} new,
                      ~{r.updated} updated, {r.unchanged} unchanged (
                      {r.totalDiscovered} total, {r.durationMs}ms)
                    </div>
                  ))
                ) : (
                  <>Discovery started — follow progress in Operation Status.</>
                )}
              </Alert>
            )}

            {discoveryPost.error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {discoveryPost.error.status === 409
                  ? "Discovery is already running. Check status below."
                  : discoveryPost.error.message}
              </Alert>
            )}

            {runningOps.length > 0 && (
              <Alert variant="info" className="mt-3 mb-0">
                <Spinner animation="border" size="sm" className="me-2" />
                {runningOps.map((op, i) => (
                  <span key={i}>
                    <strong>{op.operationKey}</strong> discovery running
                    {op.startedAt &&
                      ` (started ${new Date(op.startedAt).toLocaleTimeString()})`}
                    {i < runningOps.length - 1 && " | "}
                  </span>
                ))}
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 ms-2"
                  onClick={() => discoveryStatus.refetch()}
                >
                  Refresh status
                </Button>
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Advertisers List / Search */}
      <Card>
        <Card.Body>
          <Card.Title className="h6">Advertisers</Card.Title>
          <Row className="mb-3 g-2">
            <Col md={2}>
              <Form.Select
                size="sm"
                value={network}
                onChange={(e) => setSelectedNetwork(e.target.value || "")}
              >
                <option value="">All Networks</option>
                {advertiserNetworkOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder="Name or ID (optional)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder="Domain (optional)..."
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={handleSearch}
                disabled={
                  advertisersList.loading ||
                  (normalizedSearchInput.length < 2 &&
                    normalizedDomainInput.length < 2)
                }
              >
                <Search size={14} className="me-1" />
                Search
              </Button>
              {isSearchActive && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleClearSearch}
                >
                  Clear Search
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleRefreshAdvertisers}
                disabled={advertisersList.loading}
              >
                <RefreshCw size={14} className="me-1" />
                Load Advertisers
              </Button>
              <Button
                size="sm"
                variant="outline-success"
                onClick={handleDownloadCsv}
                disabled={downloadingCsv}
                title="Download every advertiser across all networks as CSV"
              >
                {downloadingCsv ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <Download size={14} className="me-1" />
                )}
                {downloadingCsv ? "Preparing CSV…" : "Download CSV"}
              </Button>
              {isManualNetwork && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => setShowManualAdvertiserModal(true)}
                >
                  <Plus size={14} className="me-1" />
                  Add Manual Advertiser
                </Button>
              )}
            </Col>
          </Row>

          {csvError && (
            <Alert variant="danger" className="py-2">
              {csvError}
            </Alert>
          )}

          {isLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          )}

          {advertisersList.error && (
            <Alert variant="danger">{advertisersList.error.message}</Alert>
          )}

          {!hasLoadedAdvertisers && (
            <Alert variant="secondary" className="mb-0">
              Select filters, then click Search or Load Advertisers.
            </Alert>
          )}

          {hasLoadedAdvertisers && !isLoading && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
                <small className="text-muted mb-0">
                  {isSearchActive
                    ? `${advertisersList.data?.count || 0} results for "${[committedFilters?.query, committedFilters?.domain].filter(Boolean).join(", ")}"`
                    : `${advertisersList.data?.totalCount ?? advertisersList.data?.count ?? 0} advertisers${network ? ` in ${network}` : ""}`}
                  {totalAdvertisers > 0 &&
                    ` - Showing ${pageStartIndex}-${pageEndIndex} of ${totalAdvertisers}`}
                </small>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="small mb-0">Per Page</Form.Label>
                  <Form.Select
                    size="sm"
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value)}
                    style={{ width: 90 }}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Form.Select>
                </div>
              </div>
              <AdvertiserTable
                advertisers={displayAdvertisers}
                onSelect={isManualNetwork ? undefined : handleSelectAdvertiser}
                syncConfigMap={syncConfigMap}
              />
              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-2">
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      disabled={safeCurrentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    />
                    {paginationItems.map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === safeCurrentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={safeCurrentPage >= totalPages}
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <ManualAdvertiserModal
        show={showManualAdvertiserModal}
        advertiser={null}
        onHide={() => setShowManualAdvertiserModal(false)}
        onSaved={async () => {
          await advertisersList.refetch();
        }}
      />

      {/* Sync Config Modal */}
      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Sync Configuration — {selectedAdv?.name || "—"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAdv && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <small className="text-muted d-block">Network</small>
                  <Badge bg="dark">{selectedAdv.affiliateNetwork}</Badge>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Advertiser ID</small>
                  <strong>{selectedAdv.advertiserId}</strong>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Domain</small>
                  {selectedAdv.domain || "—"}
                </Col>
              </Row>

              {syncConfigGet.loading ? (
                <div className="text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading this advertiser's sync configuration…
                </div>
              ) : selectedConfig ? (
                <>
                  <Alert variant="info" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Status:</strong>{" "}
                        {selectedConfig.productIngestionEnabled ? (
                          <Badge bg="success">Products Enabled</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">
                            Categories Only
                          </Badge>
                        )}
                        {selectedConfig.lastSyncedAt && (
                          <small className="text-muted ms-3">
                            Last synced:{" "}
                            {new Date(
                              selectedConfig.lastSyncedAt
                            ).toLocaleString()}
                          </small>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={
                          selectedConfig.productIngestionEnabled
                            ? "outline-warning"
                            : "outline-success"
                        }
                        onClick={handleToggleProducts}
                        disabled={configPost.loading}
                      >
                        {selectedConfig.productIngestionEnabled ? (
                          <>
                            <XCircle size={12} className="me-1" /> Disable
                            Products
                          </>
                        ) : (
                          <>
                            <CheckCircle size={12} className="me-1" /> Enable
                            Products
                          </>
                        )}
                      </Button>
                    </div>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Category Whitelist (comma-separated, blank = all)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="e.g. Clothing, Shoes"
                      value={modalCategories}
                      onChange={(e) => setModalCategories(e.target.value)}
                    />
                    {selectedConfig.categoryWhitelist?.length ? (
                      <Form.Text className="text-muted">
                        Currently: {selectedConfig.categoryWhitelist.join(", ")}
                      </Form.Text>
                    ) : null}
                  </Form.Group>
                </>
              ) : (
                <>
                  <Alert variant="secondary" className="mb-3">
                    <Settings size={14} className="me-1" />
                    This advertiser is <strong>not configured</strong> for
                    syncing. Create a config to start syncing categories.
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Category Whitelist (optional, comma-separated)
                    </Form.Label>
                    <Form.Control
                      placeholder="e.g. Clothing, Shoes"
                      value={modalCategories}
                      onChange={(e) => setModalCategories(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}

              {configError && (
                <Alert variant="danger" className="mb-0">
                  {configError}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateOrUpdateConfig}
            disabled={configPost.loading || syncConfigGet.loading}
          >
            {configPost.loading ? (
              <Spinner animation="border" size="sm" />
            ) : selectedConfig ? (
              "Update Config"
            ) : (
              "Create Config"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
